import React, { useState, useEffect, useCallback } from 'react';
import { Moon, Sun, X, Copy, Check, AlertCircle, ChevronLeft, ChevronRight, Play, Settings, HelpCircle } from 'lucide-react';
import { PasswordValidator } from '../ui/PasswordValidator';
import { useApp } from '../../services/store.tsx';

interface ProfileSettingsProps {
    isOpen: boolean;
    onClose: () => void;
    user: {
        name: string;
        role: string;
        churchId?: string;
    } | null;
    language: 'en' | 'de';
    theme: 'light' | 'dark';
    t: any;
    onLanguageChange: (lang: 'en' | 'de') => void;
    onThemeToggle: () => void;
    onProfileUpdate: (name: string, password?: string) => void;
}

export const ProfileSettings: React.FC<ProfileSettingsProps> = ({
    isOpen,
    onClose,
    user,
    language,
    theme,
    t,
    onLanguageChange,
    onThemeToggle,
    onProfileUpdate
}) => {
    const { getServicePin, logout, updateRemoteMappings } = useApp();
    const [editName, setEditName] = useState(user?.name || '');
    const [editPass, setEditPass] = useState('');
    const [servicePin, setServicePin] = useState<{ pin: string; expiresAt: string } | null>(null);
    const [copiedPin, setCopiedPin] = useState(false);
    const [showServicePin, setShowServicePin] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [remoteMappings, setRemoteMappings] = useState({ back: '', forward: '', play: '' });
    const [listeningFor, setListeningFor] = useState<string | null>(null);
    const [recordedKey, setRecordedKey] = useState<string | null>(null);
    const [showHelpTooltip, setShowHelpTooltip] = useState(false);

    useEffect(() => {
        if (user) setEditName(user.name);
    }, [user]);

    useEffect(() => {
        if (isOpen && user?.churchId) {
            loadServicePin();
        }
    }, [isOpen, user?.churchId]);
    useEffect(() => {
        const saved = localStorage.getItem('remoteMappings');
        if (saved) {
            setRemoteMappings(JSON.parse(saved));
        }
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (listeningFor) {
                event.preventDefault();
                const newMappings = { ...remoteMappings, [listeningFor]: event.key };
                setRemoteMappings(newMappings);
                // Save to both localStorage (for immediate persistence) and database
                localStorage.setItem('remoteMappings', JSON.stringify(newMappings));
                updateRemoteMappings(newMappings);
                setListeningFor(null);
                setRecordedKey(event.key);
                setTimeout(() => setRecordedKey(null), 5000);
            }
        };
        if (listeningFor) {
            window.addEventListener('keydown', handleKeyDown);
        }
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [listeningFor, remoteMappings, updateRemoteMappings]);
    const loadServicePin = async () => {
        try {
            const pinData = await getServicePin();
            setServicePin(pinData);
        } catch (error) {
            console.error('[ProfileSettings] Failed to load service pin:', error);
        }
    };

    const handleProfileUpdate = async () => {
        setError(null);
        try {
            await onProfileUpdate(editName, editPass);
            setEditPass('');
            onClose();
        } catch (error: any) {
            setError(error.message || 'An error occurred while updating your profile');
        }
    };

    const handleLogout = () => {
        logout();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-md mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-3">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t.users.user}</h2>
                        {servicePin && (
                            <button
                                onClick={async () => {
                                    if (showServicePin) {
                                        try {
                                            await navigator.clipboard.writeText(servicePin.pin);
                                            setCopiedPin(true);
                                            setTimeout(() => setCopiedPin(false), 2000);
                                        } catch (error) {
                                            console.error('Failed to copy service pin:', error);
                                        }
                                    }
                                    setShowServicePin(!showServicePin);
                                }}
                                className={`px-3 py-1 text-xs font-mono rounded-full border transition-colors ${
                                    showServicePin
                                        ? 'bg-brand-500/10 border-brand-500/30 text-brand-500 dark:bg-terracotta-500/20 dark:border-terracotta-500/30 dark:text-sage-500'
                                        : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                                }`}
                            >
                                {showServicePin ? servicePin.pin : 'Service PIN'}
                                {copiedPin && <span className="ml-1">✓</span>}
                            </button>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* User Settings */}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t.users.name}</label>
                        <input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="w-full px-4 py-3 text-sm border rounded-lg bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white focus:ring-brand-500 focus:border-brand-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t.users.newPass}</label>
                        <input
                            type="password"
                            value={editPass}
                            onChange={(e) => setEditPass(e.target.value)}
                            placeholder="••••••"
                            className="w-full px-4 py-3 text-sm border rounded-lg bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white focus:ring-brand-500 focus:border-brand-500"
                        />
                        <PasswordValidator password={editPass} show={editPass.length > 0} />
                    </div>

                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                                        {error}
                                    </h3>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Language Settings */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{t.users.language}</label>
                        <div className="flex gap-3">
                            <button
                                onClick={() => onLanguageChange('en')}
                                className={`flex-1 py-3 px-4 text-sm rounded-lg border transition-colors ${language === 'en' ? 'bg-brand-500/10 border-brand-500/30 text-brand-500 dark:bg-terracotta-500/20 dark:border-terracotta-500/30 dark:text-sage-500' : 'border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                            >
                                English
                            </button>
                            <button
                                onClick={() => onLanguageChange('de')}
                                className={`flex-1 py-3 px-4 text-sm rounded-lg border transition-colors ${language === 'de' ? 'bg-brand-500/10 border-brand-500/30 text-brand-500 dark:bg-terracotta-500/20 dark:border-terracotta-500/30 dark:text-sage-500' : 'border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                           >
                               Deutsch
                           </button>
                        </div>
                    </div>

                    {/* Theme Settings */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Theme</label>
                        <div className="flex gap-3">
                            <button
                                onClick={() => { if (theme === 'dark') onThemeToggle(); }}
                                className={`flex-1 py-3 px-4 text-sm rounded-lg border transition-colors flex items-center justify-center gap-2 bg-white border-gray-200 text-gray-900 ${theme === 'light' ? 'bg-brand-500/10 border-brand-500/30 text-brand-500' : ''} hover:bg-gray-50`}
                            >
                                <Sun size={20} />
                                {t.nav.lightMode}
                            </button>
                            <button
                                onClick={() => { if (theme === 'light') onThemeToggle(); }}
                                className={`flex-1 py-3 px-4 text-sm rounded-lg border transition-colors flex items-center justify-center gap-2 bg-gray-900 border-gray-800 text-white ${theme === 'dark' ? 'bg-terracotta-500/20 border-terracotta-500/30 text-sage-500' : ''} hover:bg-gray-800`}
                            >
                                <Moon size={20} />
                                {t.nav.darkMode}
                            </button>
                        </div>
                    </div>

                    {/* Remote Settings */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2 relative">
                            {t.remote.title}
                            <span
                                onMouseEnter={() => setShowHelpTooltip(true)}
                                onMouseLeave={() => setShowHelpTooltip(false)}
                                className="cursor-help"
                            >
                                <HelpCircle size={16} className="text-gray-400" />
                            </span>
                            {showHelpTooltip && (
                                <div className="absolute left-0 top-full mt-1 bg-gray-900 text-white text-sm rounded-md px-3 py-2 z-10 whitespace-normal shadow-xl border border-gray-700 max-w-xs">
                                    {t.remote.help}
                                </div>
                            )}
                        </label>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setListeningFor('back')}
                                className={`flex-1 py-3 px-4 text-sm rounded-lg border transition-colors flex items-center justify-center ${listeningFor === 'back' ? 'bg-brand-500/10 border-brand-500/30 text-brand-500 dark:bg-terracotta-500/20 dark:border-terracotta-500/30 dark:text-sage-500' : remoteMappings.back ? 'border-green-300 text-green-600 dark:border-green-700 dark:text-green-400' : 'border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                title={remoteMappings.back || t.remote.notSet}
                            >
                                <ChevronLeft size={24} />
                            </button>
                            <button
                                onClick={() => setListeningFor('play')}
                                className={`flex-1 py-3 px-4 text-sm rounded-lg border transition-colors flex items-center justify-center ${listeningFor === 'play' ? 'bg-brand-500/10 border-brand-500/30 text-brand-500 dark:bg-terracotta-500/20 dark:border-terracotta-500/30 dark:text-sage-500' : remoteMappings.play ? 'border-green-300 text-green-600 dark:border-green-700 dark:text-green-400' : 'border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                title={remoteMappings.play || t.remote.notSet}
                            >
                                <Play size={24} />
                            </button>
                            <button
                                onClick={() => setListeningFor('forward')}
                                className={`flex-1 py-3 px-4 text-sm rounded-lg border transition-colors flex items-center justify-center ${listeningFor === 'forward' ? 'bg-brand-500/10 border-brand-500/30 text-brand-500 dark:bg-terracotta-500/20 dark:border-terracotta-500/30 dark:text-sage-500' : remoteMappings.forward ? 'border-green-300 text-green-600 dark:border-green-700 dark:text-green-400' : 'border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                title={remoteMappings.forward || t.remote.notSet}
                            >
                                <ChevronRight size={24} />
                            </button>
                        </div>
                        <div className="flex gap-3 mt-1">
                            <span className="flex-1 text-center text-[10px] text-gray-500 dark:text-gray-400">{remoteMappings.back || t.remote.notSet}</span>
                            <span className="flex-1 text-center text-[10px] text-gray-500 dark:text-gray-400">{remoteMappings.play || t.remote.notSet}</span>
                            <span className="flex-1 text-center text-[10px] text-gray-500 dark:text-gray-400">{remoteMappings.forward || t.remote.notSet}</span>
                        </div>
                        {recordedKey && (
                            <div className="text-center mt-2">
                                <span className="px-3 py-1 text-xs bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded text-green-800 dark:text-green-200">
                                    {t.remote.recorded}: {recordedKey}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={handleLogout}
                            className="flex-1 py-3 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            Logout
                        </button>
                        <button
                            onClick={handleProfileUpdate}
                            className="flex-1 py-3 bg-brand-500 text-white text-sm font-medium rounded-lg hover:bg-brand-600 transition-colors"
                        >
                            {t.users.save}
                        </button>

                    </div>

                </div>
            </div>
        </div>
    );
};
